import type {
  ApiResponse,
  CreateKartRequest,
  KartListItem,
  KartStatus,
  KartSummaryStats,
  ListKartsQuery,
  PaginatedResponse,
  UpdateKartRequest,
  UpdateKartSetupHistoryEntryRequest,
  VehicleFieldTemplate,
} from '@kangaroo/shared';

import { api } from '../lib/api';

export async function fetchKarts(query: ListKartsQuery = {}) {
  const response = await api.get<PaginatedResponse<KartListItem>>('/karts', { params: query });
  return response.data;
}

export async function fetchKart(kartId: string) {
  const response = await api.get<ApiResponse<KartListItem>>(`/karts/${kartId}`);
  return response.data.data;
}

export async function fetchKartStats() {
  const response = await api.get<ApiResponse<KartSummaryStats>>('/karts/stats/summary');
  return response.data.data;
}

export async function createKart(payload: CreateKartRequest) {
  const response = await api.post<ApiResponse<KartListItem>>('/karts', payload);
  return response.data.data;
}

export async function updateKart(kartId: string, payload: UpdateKartRequest) {
  const response = await api.put<ApiResponse<KartListItem>>(`/karts/${kartId}`, payload);
  return response.data.data;
}

export async function updateKartSetupHistoryEntry(
  kartId: string,
  setupHistoryEntryId: string,
  payload: UpdateKartSetupHistoryEntryRequest,
) {
  const response = await api.put<ApiResponse<KartListItem>>(
    `/karts/${kartId}/setup-history/${setupHistoryEntryId}`,
    payload,
  );
  return response.data.data;
}

export async function createKartSetupHistoryEntry(
  kartId: string,
  payload: UpdateKartSetupHistoryEntryRequest,
) {
  const response = await api.post<ApiResponse<KartListItem>>(
    `/karts/${kartId}/setup-history`,
    payload,
  );
  return response.data.data;
}

export async function updateKartStatus(kartId: string, status: KartStatus) {
  const response = await api.patch<ApiResponse<KartListItem>>(`/karts/${kartId}/status`, { status });
  return response.data.data;
}

export async function uploadKartImage(kartId: string, image: File) {
  const formData = new FormData();
  formData.append('image', image);

  const response = await api.post<ApiResponse<KartListItem>>(`/karts/${kartId}/image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data.data;
}

export async function fetchVehicleFieldTemplates() {
  const response = await api.get<ApiResponse<VehicleFieldTemplate[]>>('/karts/templates');
  return response.data.data;
}

export async function createVehicleFieldTemplate(name: string, sourceKartId: string) {
  const response = await api.post<ApiResponse<VehicleFieldTemplate>>('/karts/templates', { name, sourceKartId });
  return response.data.data;
}

export async function applyVehicleFieldTemplate(kartId: string, templateId: string) {
  const response = await api.post<ApiResponse<KartListItem>>(`/karts/${kartId}/apply-template/${templateId}`);
  return response.data.data;
}

export function kartsListKey(query: ListKartsQuery) {
  return ['karts', query] as const;
}

export function kartDetailKey(kartId: string) {
  return ['karts', kartId] as const;
}

export const kartStatsKey = ['karts', 'stats'] as const;
export const vehicleFieldTemplatesKey = ['karts', 'templates'] as const;
